import React, { useEffect } from 'react';
import { FormField } from '../../store/slice';
import styles from './CheckboxGroupComponent.module.scss';

interface CheckboxOption {
  value: string;
  label: string;
  dependsOn?: string;
}

interface CheckboxGroupComponentProps {
  field: FormField;
  value: Record<string, boolean> | boolean; // Object for checkbox_group, boolean for single checkbox
  onChange: (name: string, value: Record<string, boolean> | boolean) => void;
  validationState?: 'valid' | 'invalid';
}

/**
 * Component for rendering checkbox groups and single checkboxes
 * Supports:
 * - Single checkbox (when field has one option) - returns boolean
 * - Multiple checkboxes (checkbox_group) - returns object like { genAi: true, consumesCredits: false }
 * - Option-level dependencies (e.g., "Consumes Credits" depends on "GenAi")
 *
 * Uses traditional checkboxes matching the Features component design
 */
export const CheckboxGroupComponent: React.FC<CheckboxGroupComponentProps> = ({
  field,
  value,
  onChange,
  validationState = 'valid'
}) => {
  const options: CheckboxOption[] = field.options || [];
  const isSingleCheckbox = options.length === 1;

  // Convert value to object format { optionValue: boolean }
  const getValueObject = (): Record<string, boolean> => {
    if (isSingleCheckbox) {
      return { [options[0].value]: value === true };
    }

    // If value is already an object, use it
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, boolean>;
    }

    // Initialize all options to false
    const result: Record<string, boolean> = {};
    options.forEach(opt => {
      result[opt.value] = false;
    });
    return result;
  };

  const valueObject = getValueObject();

  // Initialize all options to false if not already set (on first render or when value is empty)
  useEffect(() => {
    if (!isSingleCheckbox) {
      // Check if value is missing or incomplete
      const needsInitialization =
        !value ||
        typeof value !== 'object' ||
        Array.isArray(value) ||
        options.some(opt => !(opt.value in (value as Record<string, boolean>)));

      if (needsInitialization) {
        const initialValue: Record<string, boolean> = {};
        options.forEach(opt => {
          initialValue[opt.value] = false;
        });
        onChange(field.name, initialValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const handleCheckboxChange = (optionValue: string, isChecked: boolean) => {
    if (isSingleCheckbox) {
      // Single checkbox returns boolean
      onChange(field.name, isChecked);
    } else {
      // Multiple checkboxes return object with ALL options (including false values)
      // Start with all options set to false
      const newValueObject: Record<string, boolean> = {};
      options.forEach(opt => {
        newValueObject[opt.value] = valueObject[opt.value] || false;
      });

      // Update the changed option
      newValueObject[optionValue] = isChecked;

      // If unchecking an option, also uncheck any dependent options
      if (!isChecked) {
        const dependentOptions = options.filter(
          opt => opt.dependsOn === optionValue
        );
        dependentOptions.forEach(depOpt => {
          newValueObject[depOpt.value] = false;
        });
      }

      onChange(field.name, newValueObject);
    }
  };

  // Check if an option should be disabled based on its dependency
  const isOptionDisabled = (option: CheckboxOption): boolean => {
    if (!option.dependsOn) return false;
    return !valueObject[option.dependsOn];
  };

  return (
    <div
      className={`${styles.checkboxGroupContainer} ${
        validationState === 'invalid' ? styles.invalid : ''
      }`}
    >
      <label className={styles.fieldLabel}>
        {field.label}
        {field.required && <span className={styles.required}> *</span>}
      </label>

      <div className={styles.checkboxGroup}>
        {options.map(option => {
          const isSelected = valueObject[option.value] === true;
          const isDisabled = isOptionDisabled(option);

          return (
            <label key={option.value} className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={isSelected}
                disabled={isDisabled}
                onChange={e =>
                  handleCheckboxChange(option.value, e.target.checked)
                }
              />
              <span className={styles.checkboxVisual} />
              <span className={styles.checkboxText}>{option.label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
};
